import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import _ from "lodash";
import Cron from 'react-cron-generator';
import { HEADER } from 'react-cron-generator';
import { Container, Form, Button, Icon, Divider, Checkbox, Table, Dropdown, Grid, Input, Label, Modal, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";
import FuseCollapsiblePanel from '../../../components/generic/FuseCollapsiblePanel';
import FuseControlledCollapsiblePanel from "../../../components/generic/FuseControlledCollapsiblePanel";
import QuestionModal from "./QuestionModel";
import * as ACTIVATION_RULES from '../../../actions/activationRulesActions';
import { useWhatsAppTemplate, WhatsAppTemplateModal, WhatsAppTemplateField } from '../../../components/whatsapp/WhatsAppTemplateSelector';

// Constants
const DISTANCE_OPTIONS = [
  { key: '', value: '', text: 'Select distance option' },
  { key: 'along_boundary', value: 'along_boundary', text: 'Along the boundary of the polygon' },
  { key: 'any_pin', value: 'any_pin', text: 'Any PIN (such as a school)' },
  { key: 'on_boundary', value: 'on_boundary', text: 'On or within the boundary of this polygon' },
  { key: 'within_x_meters', value: 'within_x_meters', text: 'Within x meters of any point' },
  { key: 'within_x_meters_polygon_boundary', value: 'within_x_meters_polygon_boundary', text: 'Within x meters of the boundary of the polygon' },
];

const CRON_OPTIONS = {
  headers: [HEADER.HOURLY, HEADER.DAILY, HEADER.WEEKLY, HEADER.MONTHLY, HEADER.CUSTOM],
};


// Utility Functions
const updateActivationRuleState = (setState, updates) => {
  setState(prevState => ({
    ...prevState,
    activationRule: {
      ...prevState.activationRule,
      ...updates
    }
  }));
};

const validateRuleName = (name) => {
  if (!name || name.trim() === "") {
    toast.error("Please Give The Distribution Rule A Name!");
    return false;
  }
  return true;
};

// Reusable Components
const ToggleField = ({ label, checked, onChange, style = {} }) => (
  <Form.Field>
    <Checkbox
      style={{ paddingTop: '28px', ...style }}
      label={label}
      toggle
      checked={checked}
      onChange={onChange}
    />
  </Form.Field>
);

export function ActivationRulesDetails(props) {
  const [state, setState] = useState({
    activationRule: { ...props.activationRule },
    isChanged: false,
    isSchedulerCollapsed: true,
    isDemoGraphicCollapsed: true,
    isGeoGraphicCollapsed: true,
    isQuestionnaireSelectorCollapsed: true,
    isModalOpen: false,
    isModal2Open: false,
    selectedQuestionnaire: null,
    selectedQuestionnaire2: null,
    selectedQuestion: null,
    tempSelectedGeoQNaire: props.activationRule?.userLocationQuestion?.questionnaireId || null,
  });

  const {
    localTemplate,
    localInputValues,
    headerImage,
    isVerifying,
    mediaId,
    isModalOpen: isTemplateModalOpen,
    hasImageHeader,
    extractUniquePlaceholders,
    handlePlaceholderChange,
    handleImageUpload,
    handleVerifyImage,
    handleTemplateSelection,
    confirmTemplateSelection,
    closeModal: closeTemplateModal,
    addMediaIdToTemplate
  } = useWhatsAppTemplate();

  const {
    control,
    register,
    handleSubmit,
    resetField,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    props.fetchQuestionairesAction(props.tenant.id);
    props.fetchKmlFilesAction(props.tenant.id);
    props.fetchUserGroupsAction(props.tenant);
    props.fetchWhatsAppTemplatesAction(props.tenant);
  }, [props.tenant]);

  useEffect(() => {
    if (!_.isEmpty(state.selectedQuestionnaire)) {
      props.fetchQuestionsAction(state.selectedQuestionnaire.id, state.selectedQuestionnaire.tenantId);
      props.fetchSectionsAction(state.selectedQuestionnaire.id, state.selectedQuestionnaire.tenantId);
    }
  }, [state.selectedQuestionnaire]);

  useEffect(() => {
    if (!_.isEmpty(state.selectedQuestionnaire2)) {
      props.fetchQuestionsAction(state.selectedQuestionnaire2.id, state.selectedQuestionnaire2.tenantId);
      props.fetchSectionsAction(state.selectedQuestionnaire2.id, state.selectedQuestionnaire2.tenantId);
    }
  }, [state.selectedQuestionnaire2]);

  const handleWheel = (e) => {
    e.target.blur();
  };

  const onSubmit = (data) => {
    if (!validateRuleName(state.activationRule.name)) return;

    const templateToSave = addMediaIdToTemplate(
      state.activationRule.whatsAppTemplate,
      state.activationRule.whatsAppTemplateMediaId
    );

    const toCommit = {
      name: state.activationRule.name,
      description: state.activationRule.description,
      frequency: state.activationRule.frequency,
      status: state.activationRule.status || 'inActive',
      questionWithAnswers: state.activationRule.questionWithAnswers,
      userLocationQuestion: state.activationRule.userLocationQuestion,
      refKMLFile: state.activationRule.refKMLFile,
      ruleQuestionnaires: state.activationRule.ruleQuestionnaires,
      groupApplicable: state.activationRule.groupApplicable,
      ruleGroup: state.activationRule.ruleGroup,
      distanceOption: state.activationRule.distanceOption,
      distanceValue: state.activationRule.distanceValue,
      maxNumUsers: state.activationRule.maxNumUsers,
      whatsAppTemplate: templateToSave,
      whatsAppTemplatePlaceholderValues: state.activationRule.whatsAppTemplatePlaceholderValues,
      completionLimitType: props.activationRule.completionLimitType,
      timesToBeCompleted: props.activationRule.timesToBeCompleted,
      targetAudience: state.activationRule.targetAudience || 'both',
    };

    const ruleToSave = props.isNew ? toCommit : { ...state.activationRule, ...toCommit };
    props.saveActivationRule(props.tenant, ruleToSave, props.isNew);

    if (props.onComplete) props.onComplete();
  };


  const hasRefKml = !_.isEmpty(state.activationRule.refKMLFile);
  const onRemoveKml = () => {
    // clear RHF + errors
    setValue('distanceOption', '', { shouldDirty: false, shouldValidate: false });
    setValue('distanceValue', '', { shouldDirty: false, shouldValidate: false });
    clearErrors(['distanceOption', 'distanceValue']);

    // clear local mirror
    setState(prev => ({
      ...prev,
      activationRule: {
        ...prev.activationRule,
        refKMLFile: {},
        distanceOption: '',
        distanceValue: '',
        userLocationQuestion: {}
      },
      tempSelectedGeoQNaire: null, //local for geoQnaireSelection
      isGeoGraphicCollapsed: true
    }));
  };


  const onError = (errs) => {
    const geoHasError = !!(errs?.distanceOption || errs?.distanceValue);

    if (!geoHasError) return;
    if (geoHasError && hasRefKml) {
      setState(prev => ({ ...prev, isGeoGraphicCollapsed: false }));
      return;
    }

    // geo has error but NO KML -> clear geo fields + errors so form can submit
    resetField('distanceOption');
    resetField('distanceValue');
    clearErrors(['distanceOption', 'distanceValue']);
    setState(prev => ({
      ...prev,
      activationRule: {
        ...prev.activationRule,
        distanceOption: '',
        distanceValue: '',
        userLocationQuestion: {}
      },
      isGeoGraphicCollapsed: true
    }));
  };

  const toggleGeoGraphicCollapse = () =>
    setState(prevState => ({ ...prevState, isGeoGraphicCollapsed: !prevState.isGeoGraphicCollapsed }));


  const DisplaySelectedQuestions = () => {
    return (
      <div>
        <h3>Selected Demograqhic Questions and Answers</h3>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '65%' }}>Question</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '25%' }}>Answers</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }}>Remove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.activationRule.questionWithAnswers && state.activationRule.questionWithAnswers.map((selectedQuestion, index) => (
              <Table.Row key={index}>
                <Table.Cell>{selectedQuestion.question}</Table.Cell>
                <Table.Cell>
                  {selectedQuestion.answers.map((answer, answerIndex) => (
                    <span key={answerIndex}>
                      {answer.value}
                      {answerIndex < selectedQuestion.answers.length - 1 && ', '}
                    </span>
                  ))}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    compact
                    className="danger"
                    icon="remove"
                    onClick={() => {
                      const updatedQuestions = state.activationRule.questionWithAnswers.filter(
                        (question, i) => i !== index
                      );
                      setState((prevState) => ({
                        ...prevState,
                        activationRule: {
                          ...prevState.activationRule,
                          questionWithAnswers: updatedQuestions,
                        },
                      }));
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const DisplaySelectedUserSelectedQuestion = () => {
    return (
      <div>
        <br />
        <h3>Selected User Location Question</h3>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '20%' }}>Questionnaire</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '70%' }}>Question</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }}>Remove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.activationRule.userLocationQuestion && (
              <Table.Row key={state.activationRule.userLocationQuestion.questionId}>
                <Table.Cell>{state.activationRule.userLocationQuestion.questionnaire}</Table.Cell>
                <Table.Cell>{state.activationRule.userLocationQuestion.question}</Table.Cell>
                <Table.Cell>
                  <Button
                    compact
                    className="danger"
                    icon="remove"
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        activationRule: {
                          ...prevState.activationRule,
                          userLocationQuestion: {},
                        },
                      }));
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const DisplaySelectedReferenceKmlFile = () => {
    return (
      <div>
        <br />
        <h3>Selected Reference KML File</h3>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '45%' }}>Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '45%' }}>File Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }}>Remove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.activationRule.refKMLFile && (
              <Table.Row key={state.activationRule.refKMLFile.kmlId}>
                <Table.Cell>{state.activationRule.refKMLFile.name}</Table.Cell>
                <Table.Cell>{state.activationRule.refKMLFile.fileName}</Table.Cell>
                <Table.Cell>
                  <Button
                    compact
                    className="danger"
                    icon="remove"
                    onClick={onRemoveKml}
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const DisplayruleQuestionnaires = () => {
    return (
      <div>
        <br />
        <h3>Selected Questionnaires</h3>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '30%' }}>Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '60%' }}>Description</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }}>Remove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.activationRule.ruleQuestionnaires && (
              <Table.Row key={state.activationRule.ruleQuestionnaires.questionnaireId}>
                <Table.Cell>{state.activationRule.ruleQuestionnaires.questionnaire}</Table.Cell>
                <Table.Cell>{state.activationRule.ruleQuestionnaires.questionnaireDescription}</Table.Cell>
                <Table.Cell>
                  <Button
                    compact
                    className="danger"
                    icon="remove"
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        activationRule: {
                          ...prevState.activationRule,
                          ruleQuestionnaires: {},
                        },
                      }));
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const DisplaySelectedUserGroup = () => {
    return (
      <div>
        <br />
        <h3>Selected User Group: </h3>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '40%' }}>Group Name</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '40%' }}>Group Description</Table.HeaderCell>
              <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '20%' }}>Remove</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.activationRule.ruleGroup && (
              <Table.Row key={state.activationRule.ruleGroup.userGroupId}>
                <Table.Cell>{state.activationRule.ruleGroup.userGroupName}</Table.Cell>
                <Table.Cell>{state.activationRule.ruleGroup.userGroupDescription}</Table.Cell>
                <Table.Cell>
                  <Button
                    compact
                    className="danger"
                    icon="remove"
                    onClick={() => {
                      setState((prevState) => ({
                        ...prevState,
                        activationRule: {
                          ...prevState.activationRule,
                          ruleGroup: {},
                        },
                      }));
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const _buildActiveToggle = () => (
    <ToggleField
      label="Active"
      checked={state.activationRule.status === 'active'}
      onChange={() => updateActivationRuleState(setState, {
        status: state.activationRule.status === 'active' ? 'inActive' : 'active'
      })}
    />
  );

  const _buildName = () => (
    <Form.Field className="full-width-field">
      <label>Rule Name</label>
      <input
        name="name"
        className={errors.name ? "invalid-input-field" : ""}
        {...register("name", { required: true, maxLength: 128 })}
        placeholder="Name"
        value={state.activationRule.name}
        onChange={(e) => updateActivationRuleState(setState, { name: e.target.value })}
      />
      {errors.name?.type === "required" && (
        <p className="validation-error">* Please enter a name</p>
      )}
      {errors.name?.type === "maxLength" && (
        <p className="validation-error">* Name may not be more than 128 characters long</p>
      )}
    </Form.Field>
  );

  const _buildDescription = () => (
    <Form.Field className="full-width-field">
      <label>Rule Description</label>
      <input
        name="description"
        className={errors.description ? "invalid-input-field" : ""}
        {...register("description", { maxLength: 255 })}
        placeholder="Description"
        value={state.activationRule.description}
        onChange={(e) => updateActivationRuleState(setState, { description: e.target.value })}
      />
      {errors.description?.type === "maxLength" && (
        <p className="validation-error">* Description may not be more than 255 characters long</p>
      )}
    </Form.Field>
  );

  const closeTemplateModalHandler = () => {
    closeTemplateModal();
  };

  const handleTemplateSelect = (event, { value }) => {
    if (value == null) {
      setState((prevState) => ({
        ...prevState,
        activationRule: { ...prevState.activationRule, whatsAppTemplatePlaceholderValues: [] },
      }));
    } else {
      handleTemplateSelection(value);
    }
  };

  const onConfirmTemplate = (result) => {
    setState(prevState => ({
      ...prevState,
      activationRule: {
        ...prevState.activationRule,
        whatsAppTemplate: result.template,
        whatsAppTemplatePlaceholderValues: result.placeholderValues,
        whatsAppTemplateHeaderImage: result.headerImage,
        whatsAppTemplateMediaId: result.mediaId
      }
    }));
  };

  const _buildWhatsAppTemplate = () => {
    return (
      <WhatsAppTemplateField
        control={control}
        templates={props.whatsAppTemplates}
        selectedTemplate={state.activationRule.whatsAppTemplate}
        placeholderValues={state.activationRule.whatsAppTemplatePlaceholderValues}
        headerImage={state.activationRule.whatsAppTemplateHeaderImage}
        onTemplateSelect={handleTemplateSelect}
        onPlaceholderChange={handlePlaceholderChange}
      />
    );
  };

  const _maxNumUsers = () => {
    return (
      <Form.Field className="full-width-field">
        <label>Maximum Number Of Users to Receive The Questionnaire:</label>
        <input
          name="maxNum_Users"
          className={errors.maxNum_Users ? "invalid-input-field" : ""}
          {...register("maxNum_Users", {
            required: true,
            min: 1
          })}
          fluid
          type="number"
          placeholder="Maximum Number of Users to Receive The Questionnaire"
          min="1"
          value={state.activationRule.maxNumUsers || ''}
          onChange={(e) => {
            setState((prevState) => ({
              ...prevState,
              activationRule: {
                ...prevState.activationRule,
                maxNumUsers: e.target.value,
              },
            }));
          }}
          onWheel={handleWheel}
        />
        {errors.maxNum_Users &&
          errors.maxNum_Users.type === "required" && (
            <p className="validation-error">* Please Enter a value</p>
          )}
        {errors.maxNum_Users &&
          errors.maxNum_Users.type === "min" && (
            <p className="validation-error">
              * Value can't be smaller than 1
            </p>
          )}
      </Form.Field>
    );
  };

  const targetAudienceOptions = [
    { key: 'both', text: 'Mobile and WhatsApp Users', value: 'both' },
    { key: 'mobileUsers', text: 'Mobile Users Only', value: 'mobileUsers' },
    { key: 'whatsAppUsers', text: 'WhatsApp Users Only', value: 'whatsAppUsers' }
  ];

  const _targetAudience = () => {
    return (
      <Form.Field>
        <label>Target Audience (Platform)</label>
        <Dropdown
          fluid
          selection
          options={targetAudienceOptions}
          name="targetAudience"
          value={targetAudienceOptions.find(option => option.value === state.activationRule.targetAudience)?.value || 'both'}
          onChange={(e, { value }) => {
            setState((prevState) => ({
              ...prevState,
              activationRule: {
                ...prevState.activationRule,
                targetAudience: value,
              },
            }));
          }}
        />
      </Form.Field>
    );
  };

  const _buildCronScheduler = () => (
    <>
      <Form.Field>
        <label>Frequency Scheduler</label>
        <Cron
          showResultText
          name="frequency"
          onChange={(value) => updateActivationRuleState(setState, { frequency: value })}
          value={state.activationRule.frequency}
          options={CRON_OPTIONS}
        />
      </Form.Field>
      <input
        type="hidden"
        name="frequency"
        {...register("frequency")}
        value={state.activationRule.frequency}
      />
    </>
  );

  const mapQuestionTypeToLabel = (questionType) => {
    switch (questionType) {
      case 'yes_no':
        return 'Yes/No';
      case 'mc_sel_one':
        return 'Multiple Choice (Single Answer)';
      case 'multi_select':
        return 'Multiple Choice (Multiple Answers)';
      default:
        return questionType;
    }
  };

  const closeModal = () => {
    setState((prevState) => ({
      ...prevState,
      isModalOpen: false,
      selectedQuestionnaire: "",
    }))
  };

  const closeModal2 = () => {
    setState((prevState) => ({
      ...prevState,
      isModalOpen2: false,
      selectedQuestionnaire2: "",
    }))
  };

  const _buildQuestionList = (sectionId, sectionName) => {
    const { questions, sections } = props;

    // Get all subsections for the current section
    const subsections = sections.filter((section) => section.parentSectionId === sectionId);

    // Filter questions for the current section based on question types
    const filteredQuestions = questions
      .filter((question) => question.sectionId === sectionId)
      .filter((question) => ['yes_no', 'mc_sel_one', 'multi_select'].includes(question.questionType))
      .sort((a, b) => a.sequence - b.sequence);

    // Check if there are valid questions for this section
    const hasValidQuestions = filteredQuestions.length > 0;

    // Render the section and its questions only if there are valid questions
    if (hasValidQuestions) {
      return (
        <div>
          <h4>{sectionName}</h4>
          {filteredQuestions.map((question) => (
            <Table key={question.id} celled>
              <Table.Body>
                <Table.Row>
                  <Table.Cell style={{ width: '80%' }}>{question.question}</Table.Cell>
                  <Table.Cell style={{ width: '15%' }}>{question.questionType}</Table.Cell>
                  <Table.Cell style={{ width: '5%' }}>
                    <Button
                      compact
                      className='success'
                      icon='checkmark'
                      onClick={(event) => {
                        event.preventDefault();
                        props.fetchLookupsAction(question, (result, error) => {
                          try {
                            if (error) {
                              console.log("hello");
                            } else {
                              setState((state) => ({
                                ...state,
                                selectedQuestionId: question.id,
                                lookupValue: result,
                              }));
                            }
                          } catch (e) {
                            console.log(e);
                          }
                        });
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          ))}
          {/* Render subsections */}
          {subsections.map((subsection) => (
            <FuseCollapsiblePanel
              key={subsection.id}
              content={_buildQuestionList(subsection.id, subsection.name)}
              isCollapsed={true}
              leadingIcon="female"
              trailingIcon="angle down"
              header={subsection.name}
            />
          ))}
        </div>
      );
    } else {
      return null; // Return null if there are no valid questions for the section
    }
  };



  const _buildDemoQuestionList = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const { sections, questions } = props;


    const questionnaireOptions = [
      { key: 'none', value: null, text: 'Select To Hide Questions' },
      ...props.questionnaires
        .map((questionnaire) => ({
          key: questionnaire.id,
          value: questionnaire.id,
          text: questionnaire.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    ];


    const sectionOptions = [
      ...sections
        .filter((section) => !section.parentSectionId) // Filter out subsections
        .filter((section) => {
          // Check if the section contains any valid questions
          return questions.some((question) => {
            return (
              question.sectionId === section.id &&
              ['yes_no', 'mc_sel_one', 'multi_select'].includes(question.questionType)
            );
          });
        })
        .sort((a, b) => a.sequence - b.sequence)
        .map((section) => ({
          key: section.id,
          text: section.name,
          value: section,
        })),
    ];

    const filteredQuestions = (props.questions || []).filter(
      (question) =>
        question &&
        (question.questionType === 'yes_no' ||
          question.questionType === 'mc_sel_one' ||
          question.questionType === 'multi_select') &&
        (question.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          mapQuestionTypeToLabel(question.questionType).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    return (
      <Form.Field>
        <Grid >
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
              <h3>Selected Demographic Related Questions and Answers Or a User Group</h3>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={10}>
              <Form.Field>
                <label>Demographic Questions or User Group:</label>
                <Checkbox
                  style={{ paddingTop: "10px" }}
                  toggle
                  label="User Group"
                  checked={!!state.activationRule.groupApplicable}
                  onChange={() => {
                    setState((prevState) => ({
                      ...prevState,
                      activationRule: {
                        ...prevState.activationRule,
                        groupApplicable: !prevState.activationRule.groupApplicable,
                      },
                    }));
                  }}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          {state.activationRule.groupApplicable ? (
            <Grid.Row>
              <Grid.Column>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell className='nectaPrimaryBg'>
                        Group Name
                      </Table.HeaderCell>
                      <Table.HeaderCell className='nectaPrimaryBg'>
                        Group Description
                      </Table.HeaderCell>
                      <Table.HeaderCell className='nectaPrimaryBg' />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {props.userGroups.map((userGroup) => (
                      <Table.Row key={userGroup.id}>
                        <Table.Cell>{userGroup.name}</Table.Cell>
                        <Table.Cell>{userGroup.description}</Table.Cell>
                        <Table.Cell>
                          <Button
                            compact
                            className='success'
                            icon='checkmark'
                            onClick={(event) => {
                              event.preventDefault();
                              setState((state) => ({
                                ...state,
                                activationRule: {
                                  ...state.activationRule,
                                  ruleGroup: {
                                    userGroupId: userGroup.id,
                                    userGroupName: userGroup.name,
                                    userGroupDescription: userGroup.description,
                                    tenantId: userGroup.tenantId,
                                  },
                                },
                              }));
                            }}
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Grid.Column>
            </Grid.Row>
          ) : null
          }

          {!state.activationRule.groupApplicable && (
            <Grid.Row>
              <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
                <Grid.Row style={{ paddingBottom: "10px" }}>
                  <Label>Questionnaire:</Label>
                </Grid.Row>
                <Dropdown
                  placeholder="Select Questionnaire"
                  fluid
                  search
                  selection
                  options={questionnaireOptions}
                  onChange={(event, { value }) => {
                    const selectedQuestionnaire = props.questionnaires.find(
                      (questionnaire) => questionnaire.id === value
                    );

                    setState((prevState) => ({
                      ...prevState,
                      selectedQuestionnaire,
                      selectedQuestionId: null,
                      isModalOpen: true,
                    }));
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>


        {!state.activationRule.groupApplicable && state.selectedQuestionnaire && state.selectedQuestionnaire.id !== null ? (
          <>
            {!_.isEmpty(props.questions) && !_.isEmpty(filteredQuestions) ? (
              <Modal
                open={state.isModalOpen}
                onClose={closeModal}
                size="small"
              >
                <Modal.Header>Sections for Questionnaire: {state.selectedQuestionnaire.name}</Modal.Header>
                <Modal.Content>
                  {sectionOptions.map((section, index) => (
                    <div key={section.key}>
                      <FuseCollapsiblePanel
                        content={_buildQuestionList(section.key, section.text)}
                        isCollapsed={true}
                        leadingIcon="book"
                        trailingIcon="angle down"
                        header={section.text}
                      />
                      {index !== sectionOptions.length - 1 && <div style={{ marginBottom: '10px' }}></div>}
                    </div>
                  ))}
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={closeModal} negative>
                    Close
                  </Button>
                </Modal.Actions>
              </Modal>
            ) : (
              <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                No relevant questions for the selected questionnaire.
              </div>
            )}

            {state.selectedQuestionId && (
              <QuestionModal
                question={props.questions.find(
                  (question) => question.id === state.selectedQuestionId
                )}
                onClose={() =>
                  setState((prevState) => ({
                    ...prevState,
                    selectedQuestionId: null,
                  }))
                }
                onSave={(answersWithQuestion) => {
                  const updatedAnswers = JSON.parse(answersWithQuestion);

                  // Check if the question is already in the list
                  const existingQuestionIndex = state.activationRule.questionWithAnswers.findIndex(
                    (existingAnswer) => existingAnswer.questionId === state.selectedQuestionId
                  );

                  if (existingQuestionIndex !== -1) {
                    // If the question exists, override it with the new one
                    const newQuestionWithAnswers = [...state.activationRule.questionWithAnswers];
                    newQuestionWithAnswers[existingQuestionIndex] = updatedAnswers[0];
                    setState((state) => ({
                      ...state,
                      activationRule: {
                        ...state.activationRule,
                        questionWithAnswers: newQuestionWithAnswers,
                      },
                    }));
                    toast.success("Question Added")
                  } else {
                    // If the question is not in the list, add it
                    setState((state) => ({
                      ...state,
                      activationRule: {
                        ...state.activationRule,
                        questionWithAnswers: [...state.activationRule.questionWithAnswers, ...updatedAnswers],
                      },
                    }));
                    toast.success("Question Added")
                  }
                }}

                lookupValue={state.lookupValue}
              />
            )}
          </>
        ) : null
        }
        <Divider />
        {state.activationRule.questionWithAnswers && !state.activationRule.groupApplicable && state.activationRule.questionWithAnswers.length > 0 && <DisplaySelectedQuestions />}
        {!_.isEmpty(state.activationRule.ruleGroup) && state.activationRule.groupApplicable && <DisplaySelectedUserGroup />}
      </Form.Field>
    );
  };

  const _buildQuestionList2 = (sectionId, sectionName) => {
    const { questions, sections } = props;

    // Get all subsections for the current section
    const subsections = sections.filter((section) => section.parentSectionId === sectionId);

    // Filter questions for the current section based on question types
    const filteredQuestions = questions
      .filter((question) => question.sectionId === sectionId)
      .filter((question) => ['location'].includes(question.questionType))
      .sort((a, b) => a.sequence - b.sequence);

    // Render the section and its questions only if there are questions
    if (filteredQuestions.length > 0) {
      return (
        <div>
          <h4>{sectionName}</h4>
          {filteredQuestions.map((question) => (
            <Table key={question.id} celled>
              <Table.Body>
                <Table.Row>
                  <Table.Cell style={{ width: '90%' }}>{question.question}</Table.Cell>
                  <Table.Cell style={{ width: '10%' }}>
                    <Button
                      compact
                      className='success'
                      icon='checkmark'
                      onClick={(event) => {
                        event.preventDefault();
                        setState((state) => ({
                          ...state,
                          activationRule: {
                            ...state.activationRule,
                            userLocationQuestion: {
                              questionId: question.id,
                              questionnaireId: question.questionaireId,
                              question: question.question,
                              questionnaire: question.questionaireName,
                              tenantId: question.tenantId,
                            },
                          },
                        }));
                        toast.success("Question Added")
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          ))}
          {/* Render subsections */}
          {subsections.map((subsection) => (
            <FuseCollapsiblePanel
              key={subsection.id}
              content={_buildQuestionList(subsection.id, subsection.name)}
              isCollapsed={true}
              leadingIcon="female"
              trailingIcon="angle down"
              header={subsection.name}
            />
          ))}
        </div>
      );
    } else {
      return null; // Return null if there are no questions for the section
    }
  };

  const _buildUserLocationList = () => {
    const { sections, questions } = props;

    const filteredQuestions = (props.questions || []).filter(
      (question) =>
        question &&
        (question.questionType === 'location')
    );

    const questionnaireOptions = [
      { key: 'none', value: null, text: 'Select To Hide Questions' },
      ...props.questionnaires
        .map((questionnaire) => ({
          key: questionnaire.id,
          value: questionnaire.id,
          text: questionnaire.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    ];

    const sectionOptions = [
      ...sections
        .filter((section) => !section.parentSectionId) // Filter out subsections
        .filter((section) => {
          // Check if the section contains any valid questions
          return questions.some((question) => {
            return (
              question.sectionId === section.id &&
              ['location'].includes(question.questionType)
            );
          });
        })
        .sort((a, b) => a.sequence - b.sequence)
        .map((section) => ({
          key: section.id,
          text: section.name,
          value: section,
        })),
    ];

    return (
      <Form.Field>
        <Grid>
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
              <h3>Select User Location Question</h3>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
              <label>Distance Options:</label>
              <Controller
                defaultValue={state.activationRule.distanceOption}
                name="distanceOption"
                control={control}
                rules={{
                  validate: value =>
                    !hasRefKml || (value !== '' && value !== null && value !== undefined) || 'Please select a distance option'
                }}
                render={({ field: { ref, onChange, value, ...restField } }) => (
                  <Dropdown
                    {...restField}
                    value={value ?? ''}
                    placeholder="Select Distance Option"
                    fluid
                    selection
                    options={DISTANCE_OPTIONS}
                    onChange={(event, { value }) => {
                      onChange(value);
                      resetField('distanceValue');
                      updateActivationRuleState(setState, {
                        distanceOption: value,
                        distanceValue: ''
                      });
                    }}
                    input={{ ref }}
                  />
                )}
              />

              {errors.distanceOption && (
                <p className="validation-error">* Please select a distance option</p>
              )}
            </Grid.Column>
            {(state.activationRule.distanceOption === 'within_x_meters' || state.activationRule.distanceOption === "within_x_meters_polygon_boundary") && (
              <Grid.Column width={6} style={{ paddingBottom: "10px" }}>
                <label>Enter distance (in meters):</label>
                <Controller
                  name="distanceValue"
                  control={control}
                  rules={{
                    validate: value => {
                      if (!hasRefKml) return true; // not required if no KML
                      const distanceOption = state.activationRule.distanceOption;
                      if (distanceOption !== 'within_x_meters' && distanceOption !== 'within_x_meters_polygon_boundary') return true;
                      if (value === '' || value === undefined || value === null) return 'Distance is required';
                      if (Number(value) < 1) return 'Distance must be at least 1 meter';
                      return true;
                    }
                  }}
                  defaultValue={state.activationRule.distanceValue}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}          // <-- controlled value
                      min={1}
                      fluid
                      type="number"
                      placeholder="Enter distance"
                      onChange={(e, { value }) => {
                        field.onChange(value);           // update RHF
                        setState(prevState => ({         // mirror to component state
                          ...prevState,
                          activationRule: {
                            ...prevState.activationRule,
                            distanceValue: value,
                          }
                        }));
                      }}
                    />
                  )}
                />
                {errors.distanceValue && (
                  <p className="validation-error">
                    * {errors.distanceValue.message}
                  </p>
                )}
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
              <label>Questionnaire:</label>
              <Dropdown
                placeholder="Select Questionnaire"
                fluid
                search
                selection
                value={state.tempSelectedGeoQNaire}         // <-- controlled by temp only
                options={questionnaireOptions}
                onChange={(event, { value }) => {
                  const selectedQuestionnaire2 = props.questionnaires.find(
                    (questionnaire) => questionnaire.id === value);
                  setState(prevState => ({
                    ...prevState,
                    selectedQuestionnaire2,
                    selectedQuestionId: null,
                    isModalOpen2: true,
                    tempSelectedGeoQNaire: value
                  }));
                }}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {state.selectedQuestionnaire2 && state.selectedQuestionnaire2.id !== null ? (
          <>
            {!_.isEmpty(props.questions) && !_.isEmpty(filteredQuestions) ? (
              <Modal
                open={state.isModalOpen2}
                onClose={closeModal2}
                size="small"
              >
                <Modal.Header>Sections for Questionnaire: {state.selectedQuestionnaire2.name}</Modal.Header>
                <Modal.Content>
                  {sectionOptions.map((section, index) => (
                    <div key={section.key}>
                      <FuseCollapsiblePanel
                        content={_buildQuestionList2(section.key, section.text)}
                        isCollapsed={true}
                        leadingIcon="book"
                        trailingIcon="angle down"
                        header={section.text}
                      />
                      {index !== sectionOptions.length - 1 && <div style={{ marginBottom: '10px' }}></div>}
                    </div>
                  ))}
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={closeModal2} negative>
                    Close
                  </Button>
                </Modal.Actions>
              </Modal>
            ) : (
              <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                No relevant questions for the selected questionnaire.
              </div>
            )}
          </>
        ) : null
        }
        {!_.isEmpty(state.activationRule.userLocationQuestion) && <DisplaySelectedUserSelectedQuestion />}
      </Form.Field>
    );
  };

  const _buildKmlList = () => {
    const filteredQuestions = (props.questions || []).filter(
      (question) =>
        question &&
        (question.questionType === 'location')
    );

    return (
      <Form.Field>
        <Grid>
          <Grid.Row>
            <Grid.Column width={10} style={{ paddingBottom: "10px" }}>
              <h3>Select Reference KML File</h3>
              <label >Reference KML File:</label>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {!_.isEmpty(props.kmlFiles) && (
          <div>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '30%' }}>
                    Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '30%' }}>
                    Description
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '30%' }}>
                    File Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {props.kmlFiles.map((file) => (
                  <Table.Row key={file.id}>
                    <Table.Cell>{file.name}</Table.Cell>
                    <Table.Cell>{file.description}</Table.Cell>
                    <Table.Cell>{file.fileName}</Table.Cell>
                    <Table.Cell>
                      <Button
                        compact
                        className='success'
                        icon='checkmark'
                        onClick={(event) => {
                          event.preventDefault();
                          setState((state) => ({
                            ...state,
                            activationRule: {
                              ...state.activationRule,
                              refKMLFile: {
                                kmlId: file.id,
                                name: file.name,
                                fileName: file.fileName,
                                filePath: file.filePath,
                                tenantId: file.tenantId,
                              },
                            },
                          }));
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
        {!_.isEmpty(state.activationRule.refKMLFile) && <DisplaySelectedReferenceKmlFile />}
      </Form.Field>
    );
  };

  const _buildQuestionnaireselector = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);

    const filteredQuestionnaires = props.questionnaires
      .filter(
        (questionnaire) =>
          questionnaire.status === "active" &&
          !selectedQuestionnaires.some(
            (selectedQ) => selectedQ.questionnaireId === questionnaire.id
          ) &&
          (
            questionnaire.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            questionnaire.description.toLowerCase().includes(searchQuery.toLowerCase())
          )
      )
      .sort((a, b) => a.name.localeCompare(b.name));



    return (
      <Form.Field>
        <Grid>
          <Grid.Row>
            <Grid.Column style={{ paddingBottom: "10px" }}>
              <h3>Select Questionnaires</h3>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Input
          icon='search'
          placeholder='Search...'
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
        />
        <Divider />
        {!_.isEmpty(filteredQuestionnaires) ? (
          <div>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '25%' }}>
                    Questionnaire Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '55%' }}>
                    Questionnaire Description
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }}>
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell className='nectaPrimaryBg' style={{ width: '10%' }} />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredQuestionnaires.map((questionnaire) => (
                  <Table.Row key={questionnaire.id}>
                    <Table.Cell>{questionnaire.name}</Table.Cell>
                    <Table.Cell>{questionnaire.description}</Table.Cell>
                    <Table.Cell>{questionnaire.status}</Table.Cell>
                    <Table.Cell>
                      <Button
                        compact
                        className='success'
                        icon='checkmark'
                        onClick={(event) => {
                          event.preventDefault();
                          const updatedSelectedQuestionnaires = [
                            {
                              questionnaireId: questionnaire.id,
                              questionnaire: questionnaire.name,
                              questionnaireDescription: questionnaire.description,
                              tenantId: questionnaire.tenantId,
                            },
                          ];

                          setState((state) => ({
                            ...state,
                            activationRule: {
                              ...state.activationRule,
                              ruleQuestionnaires: {
                                questionnaireId: questionnaire.id,
                                questionnaire: questionnaire.name,
                                questionnaireDescription: questionnaire.description,
                                tenantId: questionnaire.tenantId,
                              },
                            },
                          }));

                          // Update selectedQuestionnaires in the state
                          setSelectedQuestionnaires(updatedSelectedQuestionnaires);
                        }}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f8d7da', color: '#721c24' }}>
            No questionnaires matching your search query
          </div>
        )}
        {!_.isEmpty(state.activationRule.ruleQuestionnaires) && <DisplayruleQuestionnaires />}
      </Form.Field>
    );
  };

  return (
    <Container>
      <Segment>
        <Form
          size="small"
          onSubmit={handleSubmit(onSubmit, onError)}
          style={{ paddingBottom: "30px" }}
        >
          <Form.Group widths="equal">
            <Grid>
              <Grid.Column width={14}>
                {_buildName()}
              </Grid.Column>
              <Grid.Column width={2}>
                {_buildActiveToggle()}
              </Grid.Column>
            </Grid>
          </Form.Group>

          <Form.Group widths="equal">
            {_buildDescription()}
          </Form.Group>

          <Form.Group widths="equal">
            {_buildWhatsAppTemplate()}
          </Form.Group>

          <Form.Group widths="equal">
            <Grid>
              <Grid.Column width={6}>
                {_maxNumUsers()}
              </Grid.Column>
              <Grid.Column width={8}>
                {_targetAudience()}
              </Grid.Column>
            </Grid>
          </Form.Group>
          <Divider />
          <FuseControlledCollapsiblePanel
            header="Geographic Questions"
            leadingIcon="map"
            trailingIcon="angle down"
            isCollapsed={state.isGeoGraphicCollapsed}
            onToggle={toggleGeoGraphicCollapse}
            content={
              <>
                {_buildUserLocationList()}
                <Divider />
                {_buildKmlList()}
              </>
            }
          />
          <Divider />
          <FuseCollapsiblePanel
            content={
              <>
                {/* {_buildTimesToComplete()} */}
                {_buildCronScheduler()}
              </>
            }
            isCollapsed={state.isSchedulerCollapsed}
            // onToggle={toggleFrequencyCollapse}
            leadingIcon="clock"
            trailingIcon="angle down"
            header="Frequency Scheduler"
          />
          <Divider />
          <FuseCollapsiblePanel
            content={_buildDemoQuestionList()}
            isCollapsed={state.isDemoGraphicCollapsed}
            // onToggle={toggleDemoGraphicCollapse}
            leadingIcon="male"
            trailingIcon="angle down"
            header="Demographic Questions"
          />
          <Divider />
          <FuseCollapsiblePanel
            content={
              <>
                {_buildQuestionnaireselector()}
              </>
            }
            isCollapsed={state.isQuestionnaireSelectorCollapsed}
            // onToggle={toggleQuestionnaireSelectorCollapse}
            leadingIcon="question"
            trailingIcon="angle down"
            header="Questionnaire Selector"
          />
          <Divider />
          <Button.Group floated="right" >
            <Button
              type="button"
              className="danger"
              onClick={(e) => {
                e.preventDefault();
                if (props.onComplete) {
                  props.onComplete();
                }
              }}
            >
              <Icon name="remove" /> Cancel
            </Button>
            <Button type="submit" className="success">
              <Icon name="checkmark" /> Ok
            </Button>
          </Button.Group>
        </Form>
      </Segment>

      <WhatsAppTemplateModal
        isOpen={isTemplateModalOpen}
        template={localTemplate}
        headerImage={headerImage}
        mediaId={mediaId}
        isVerifying={isVerifying}
        onClose={closeTemplateModalHandler}
        onConfirm={() => confirmTemplateSelection(onConfirmTemplate)}
        onImageUpload={handleImageUpload}
        onVerifyImage={() => handleVerifyImage(props.tenant.id, 'ActivationRules/TemplateImages')}
        onPlaceholderChange={handlePlaceholderChange}
        extractPlaceholders={extractUniquePlaceholders}
        hasImageHeader={hasImageHeader}
      />
    </Container>
  );
}

const mapStateToProps = (state, ownProps) => {
  let activationRule = {};
  let isNew = false;
  let newRule = {
    name: '',
    description: '',
    frequency: '',
    timesToBeCompleted: '',
    status: "inActive",
    isActive: true,
    questionWithAnswers: [],
    userLocationQuestion: {},
    ruleQuestionnaires: {},
    refKMLFile: {},
    ruleGroup: {},
    completionLimitType: "byTimes",
    timesToBeCompleted: "1",
    // anonymisedData: false,
    // whatsAppTemplate: {},
    groupApplicable: false,
    tenantId: state.tenantManagement.activeTenant.id,
  };

  if (ownProps.bdaction === 'add') {
    activationRule = newRule;
    isNew = true;
  } else {
    activationRule = _.isEmpty(ownProps.activationRule)
      ? newRule
      : ownProps.activationRule;
    isNew = false;
  }

  return {
    tenant: state.tenantManagement.activeTenant,
    isNew: isNew,
    activationRule: activationRule,
    questionnaires: _.isEmpty(state.activationRules.questionnaires) ? [] : Object.values(state.activationRules.questionnaires),
    sections: _.isEmpty(state.activationRules.sections) ? [] : Object.values(state.activationRules.sections),
    questions: _.isEmpty(state.activationRules.questions) ? [] : Object.values(state.activationRules.questions),
    kmlFiles: _.isEmpty(state.activationRules.kmlFiles) ? [] : Object.values(state.activationRules.kmlFiles),
    whatsAppTemplates: _.isEmpty(state.activationRules.whatsAppTemplates) ? [] : Object.values(state.activationRules.whatsAppTemplates),
    userGroups: _.isEmpty(state.activationRules.userGroups) ? [] : Object.values(state.activationRules.userGroups),
  };
};

export default connect(
  // map state to props
  mapStateToProps,
  // map dispatch to props
  (dispatch) => ({
    saveActivationRule: (tenant, rule, isNew) => dispatch(ACTIVATION_RULES.saveActivationRuleAction(tenant, rule, isNew)),
    fetchQuestionairesAction: (tenant) => dispatch(ACTIVATION_RULES.fetchQuestionnairesAction(tenant)),
    fetchKmlFilesAction: (tenant) => dispatch(ACTIVATION_RULES.fetchKmlFilesAction(tenant)),
    fetchWhatsAppTemplatesAction: (tenant) => dispatch(ACTIVATION_RULES.fetchWhatsAppTemplatesAction(tenant)),
    fetchQuestionsAction: (questionnaireId, tenantId) => dispatch(ACTIVATION_RULES.fetchQuestionsAction(questionnaireId, tenantId)),
    fetchSectionsAction: (questionnaireId, tenantId) => dispatch(ACTIVATION_RULES.fetchSectionsAction(questionnaireId, tenantId)),
    fetchLookupsAction: (question, next) => dispatch(ACTIVATION_RULES.fetchLookupsAction(question, next)),
    fetchUserGroupsAction: (tenant) => dispatch(ACTIVATION_RULES.fetchUserGroupsAction(tenant)),
  })
)(ActivationRulesDetails);